import {Swipe, Page} from '@kit';

import React from 'react';
import {gql} from '@apollo/client';
import {useApolloClient} from '@apollo/client/react';

import {ClassFeatures} from '../features';
import {ClassAbout} from '../about';
import {ClassSpells} from '../spells';
import {ClassSubClasses} from '../sub-classes';
import {ClassSection} from '../section';
import {Class, useClassQuery} from '../../../generated/types';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {ClassSeo} from '../seo';
import {PageTools} from '../../../components/page-tools';
import {NotFound} from '../../../components/not-found';
import {useRoute} from '../../../lib/routes';

import dashboardCss from '@kit/sidebar/dashboard.module.css';

type Props = {
	classId: string;
	title: string;
};

export const ClassLayout: React.FC<Props> = ({classId, title}) => {
	const client = useApolloClient();
	const heroClass = client.readFragment<{
		id: string;
		title: string;
		hasSpells: boolean;
		sections: Array<{id: string; type: string; title: string; description: string}> | null;
	}>({
		id: `Class:${classId}`,
		fragment: gql`
			fragment MyClass on Class {
				id
				title
				hasSpells
				sections
			}
		`,
	});

	// тот же запрос, что у вкладок: из кэша Apollo, без второго похода в сеть
	const {data, loading, error} = useClassQuery({variables: {id: classId}, ssr: true});
	const {routeName, params} = useRoute();
	// адрес особенности, которой у класса нет, — это не страница класса, а несуществующая страница
	const unknownFeature =
		routeName === 'classFeature' &&
		!loading &&
		!!data?.class &&
		!data.class.features?.some((feature) => feature.id === params.featureId);
	const notFound = (!loading && !data?.class && isNotFoundError(error)) || unknownFeature;
	useNotFound(notFound);

	const pages: Page[] = [
		{
			route: 'classInfo',
			routeParams: {id: classId},
			title: 'Описание',
			affectedRoutes: ['classInfo'],
			component: ClassAbout,
			props: {classId},
		},
		{
			route: 'class',
			routeParams: {id: classId},
			title: 'Особенности',
			affectedRoutes: ['class', 'classFeature'],
			component: ClassFeatures,
			props: {classId},
		},
		{
			route: 'subClasses',
			routeParams: {id: classId},
			title: 'Подклассы',
			affectedRoutes: ['subClasses'],
			component: ClassSubClasses,
			props: {classId},
		},
	];

	if (heroClass?.sections) {
		heroClass.sections.map((section: {id: string; type: string; title: string; description: string}) => {
			switch (section.type) {
				case 'Metamagic':
					pages.push({
						route: 'classMetaMagics',
						routeParams: {id: classId},
						title: section.title,
						affectedRoutes: ['classMetaMagics', 'classMetaMagicFeature'],
						component: ClassSection,
						props: {classId},
					});
					break;
				case 'Invocation':
					pages.push({
						route: 'classInvocations',
						routeParams: {id: classId},
						title: section.title,
						affectedRoutes: ['classInvocations', 'classInvocationFeature'],
						component: ClassSection,
						props: {classId},
					});
					break;
				case 'Maneuver':
					pages.push({
						route: 'classManeuvers',
						routeParams: {id: classId},
						title: section.title,
						affectedRoutes: ['classManeuvers', 'classManeuverFeature'],
						component: ClassSection,
						props: {classId},
					});
					break;
				case 'ArcaneShot':
					pages.push({
						route: 'classArcaneShots',
						routeParams: {id: classId},
						title: section.title,
						affectedRoutes: ['classArcaneShots', 'classArcaneShotFeature'],
						component: ClassSection,
						props: {classId},
					});
					break;
				case 'WildMagic':
					pages.push({
						route: 'classWildMagic',
						routeParams: {id: classId},
						title: section.title,
						affectedRoutes: ['classWildMagic', 'classWildMagicFeature'],
						component: ClassSection,
						props: {classId},
					});
					break;
			}
		});
	}

	if (heroClass?.hasSpells)
		pages.push({
			route: 'classSpells',
			routeParams: {id: classId},
			title: 'Заклинания',
			affectedRoutes: ['classSpells'],
			component: ClassSpells,
			props: {classId},
		});

	return (
		<article className={dashboardCss.viewWrapper}>
			{data?.class && <ClassSeo heroClass={data.class as Class} />}
			{notFound && <NotFoundSeo />}
			{/* при серверной отрисовке страница ещё не знает, какой класс выбран, поэтому название берём из него самого */}
			<PageTools />
			<h1 className={dashboardCss.mainTitle}>{data?.class?.title || title}</h1>
			{notFound ? <NotFound /> : <Swipe pages={pages} />}
		</article>
	);
};
